import { useEffect, useMemo, useState } from "react";
import type { DisplayThemeMode } from "@/lib/montageplanDisplay";
import {
  DISPLAY_THEME_UNLOCK,
  setDisplayThemeLock,
} from "@/theme/displayThemeLock";
import type { EffectiveTheme } from "@/theme/themeContext";

function resolveEffective(mode: DisplayThemeMode, systemDark: boolean): EffectiveTheme {
  if (mode === "system") return systemDark ? "dark" : "light";
  return mode;
}

export function useDisplayDeviceTheme(themeMode: DisplayThemeMode | null | undefined) {
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false,
  );

  useEffect(() => {
    if (themeMode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handle = () => setSystemDark(mq.matches);
    mq.addEventListener("change", handle);
    return () => mq.removeEventListener("change", handle);
  }, [themeMode]);

  const effective = useMemo(
    () => resolveEffective(themeMode ?? "dark", systemDark),
    [themeMode, systemDark],
  );

  useEffect(() => {
    setDisplayThemeLock(effective);
    return () => {
      setDisplayThemeLock(null);
      window.dispatchEvent(new CustomEvent(DISPLAY_THEME_UNLOCK));
    };
  }, [effective]);

  return effective;
}
